import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Clock, User, CheckCircle, Play } from 'lucide-react'

const GuideDetailPage = () => {
  const { slug } = useParams()

  // Guide content database
  const guides = {
    'first-conversation-with-ai': {
      title: 'First Conversation with AI',
      description: 'Step-by-step guide to having your first English conversation with AI',
      difficulty: 'beginner',
      readTime: '5 min read',
      author: 'VivaLingua Team',
      lastUpdated: 'December 2024',
      content: `
# Getting Started with Your First AI Conversation

Welcome to your first English conversation with AI! This guide will walk you through everything you need to know to have a successful and confidence-building experience.

## What You'll Learn
- How to start a conversation with AI
- Common phrases to use
- How to handle mistakes
- Tips for improving your experience

## Before You Begin

Make sure you have:
- A quiet environment
- Working microphone and speakers
- Positive mindset and patience with yourself

## Step 1: Choose Your Conversation Topic

Start with simple, familiar topics such as:
- **Introducing yourself** - "Hello, my name is..."
- **Talking about your day** - "Today I went to..."
- **Describing your hobbies** - "I enjoy..."
- **Asking about the weather** - "How's the weather today?"

> **Tip:** Choose topics you're comfortable discussing in your native language first.

## Step 2: Practice Basic Phrases

Here are some essential phrases to get you started:

### Greetings
- "Hello, how are you today?"
- "Good morning/afternoon/evening!"
- "Nice to meet you!"

### Asking for Help
- "Could you please repeat that?"
- "I didn't understand. Can you explain?"
- "How do you pronounce this word?"

### Expressing Uncertainty
- "I'm not sure about this."
- "Let me think about that."
- "Could you help me with this?"

## Step 3: Start the Conversation

1. **Take a deep breath** - Relax and remember that AI is patient
2. **Begin with a greeting** - Start simple with "Hello" or "Hi"
3. **Introduce yourself** - "My name is [your name]"
4. **Ask a question** - Keep the conversation flowing

## Step 4: Don't Worry About Mistakes

Remember:
- Making mistakes is part of learning
- AI teachers are designed to be patient
- You can always ask for clarification
- Practice makes perfect

## Common Challenges and Solutions

### Challenge: "I'm too nervous to speak"
**Solution:** Start by reading aloud first, then gradually move to spontaneous conversation.

### Challenge: "I don't understand the AI's response"
**Solution:** Ask the AI to speak slower or explain in simpler terms.

### Challenge: "I run out of things to say"
**Solution:** Prepare a list of topics or questions beforehand.

## Advanced Tips

Once you're comfortable with basic conversations:

1. **Ask for feedback** - "How was my pronunciation?"
2. **Request corrections** - "Please correct my grammar"
3. **Practice specific skills** - "Can we work on past tense?"
4. **Try role-playing** - "Let's pretend we're at a restaurant"

## What's Next?

After completing your first conversation:
- Practice regularly (even 10 minutes daily)
- Try different conversation topics
- Work on specific areas like pronunciation or grammar
- Build up to longer conversations

## Troubleshooting

If you encounter technical issues:
- Check your microphone permissions
- Ensure stable internet connection
- Try refreshing the page
- Contact support if problems persist

---

**Congratulations!** You're now ready to have your first conversation with AI. Remember, every expert was once a beginner. Take your time, be patient with yourself, and enjoy the learning process.
      `
    },
    'common-interview-questions': {
      title: 'Common Interview Questions & Answers',
      description: 'Master 30 essential interview questions with professional sample answers',
      difficulty: 'intermediate',
      readTime: '15 min read',
      author: 'VivaLingua Team',
      lastUpdated: 'December 2024',
      content: `
# Master Common Interview Questions & Answers

Job interviews can be nerve-wracking, especially when English isn't your first language. This comprehensive guide covers 30 of the most common interview questions with professional sample answers to help you succeed.

## How to Use This Guide

- **Read each question and answer carefully**
- **Practice speaking the answers out loud**
- **Adapt the answers to your own experience**
- **Use AI conversation practice to rehearse**

> **Important:** These are sample answers. Personalize them with your own experiences and achievements.

---

## Personal Background Questions

### 1. Tell me about yourself.

**Sample Answer:**
"I'm a dedicated professional with 5 years of experience in customer service. I enjoy solving problems and making processes more efficient. Outside of work, I enjoy reading and improving my skills through online courses."

**Practice Tips:**
- Keep it professional but personal
- Mention relevant experience
- Include what motivates you

### 2. What are your strengths?

**Sample Answer:**
"I'm highly organized, a strong communicator, and great at working in teams. I'm also very good at staying calm under pressure."

**Practice Tips:**
- Choose 3-4 relevant strengths
- Give specific examples when possible
- Connect strengths to the job requirements

### 3. What are your weaknesses?

**Sample Answer:**
"Sometimes I take on too much because I like to help others, but I've learned to manage my time better and delegate when necessary."

**Practice Tips:**
- Choose a real weakness
- Show how you're working to improve
- Don't say "I'm a perfectionist"

### 4. What motivates you?

**Sample Answer:**
"I'm motivated by solving problems, helping others, and achieving goals. I also enjoy learning and improving."

**Practice Tips:**
- Be genuine and specific
- Connect to work-related motivations
- Show enthusiasm

---

## Job-Specific Questions

### 5. Why do you want this job?

**Sample Answer:**
"I admire your company's focus on innovation and customer satisfaction. This role matches my skills and interests perfectly, and I'm excited about the opportunity to contribute."

**Practice Tips:**
- Research the company beforehand
- Connect your skills to the role
- Show genuine interest

### 6. Why should we hire you?

**Sample Answer:**
"I have the skills and attitude you're looking for. I'm quick to learn, I work well with others, and I'm passionate about delivering great results."

**Practice Tips:**
- Highlight unique qualifications
- Show confidence without arrogance
- Give specific examples

### 7. What do you know about our company?

**Sample Answer:**
"I know you're a leader in your field, known for your commitment to innovation and quality. I admire your recent projects and your company values."

**Practice Tips:**
- Research the company thoroughly
- Mention specific recent news or projects
- Connect company values to your own

---

## Experience & Career Questions

### 8. Why did you leave your last job?

**Sample Answer:**
"I enjoyed my time there, but I'm looking for new challenges and opportunities to grow professionally."

**Practice Tips:**
- Stay positive about previous employers
- Focus on growth and opportunities
- Don't complain about past jobs

### 9. Where do you see yourself in 5 years?

**Sample Answer:**
"I hope to grow into a leadership role where I can help others succeed and contribute to long-term company goals."

**Practice Tips:**
- Show ambition but be realistic
- Align with company growth opportunities
- Demonstrate long-term thinking

### 10. What are your career goals?

**Sample Answer:**
"My goal is to continue learning and take on more responsibility, eventually moving into a leadership position."

**Practice Tips:**
- Be specific about your aspirations
- Show how the role fits your goals
- Demonstrate growth mindset

---

## Behavioral Questions

### 11. Tell me about a challenge you faced and how you handled it.

**Sample Answer:**
"In my last role, a big client was unhappy with a delivery. I took ownership, communicated clearly, and worked with the team to solve it quickly. We kept the client and improved our process."

**Practice Tips:**
- Use the STAR method (Situation, Task, Action, Result)
- Choose a relevant professional example
- Focus on your problem-solving skills

### 12. How do you handle pressure or stress?

**Sample Answer:**
"I stay calm, break tasks into small steps, and prioritize what's most important. I've learned that staying organized is key to managing pressure."

**Practice Tips:**
- Give specific stress management techniques
- Show you work well under pressure
- Include examples if possible

### 13. How do you handle criticism?

**Sample Answer:**
"I see criticism as a chance to grow. I listen carefully, ask questions if needed, and use the feedback to improve."

**Practice Tips:**
- Show you're open to feedback
- Demonstrate learning from criticism
- Give a specific example

### 14. How do you handle failure?

**Sample Answer:**
"I try to understand what went wrong, learn from it, and use the experience to do better next time."

**Practice Tips:**
- Show resilience and learning ability
- Demonstrate growth from setbacks
- Keep examples professional

---

## Work Style Questions

### 15. Do you prefer working in a team or alone?

**Sample Answer:**
"I enjoy both. I like collaborating with others, but I'm also comfortable working independently when needed."

**Practice Tips:**
- Show flexibility in work style
- Give examples of both scenarios
- Match the role requirements

### 16. How do you prioritize your work?

**Sample Answer:**
"I start by identifying urgent tasks and deadlines. I use tools like task lists or calendars to stay on track."

**Practice Tips:**
- Describe your specific organization methods
- Show you understand priorities
- Mention tools or systems you use

### 17. How do you deal with a difficult coworker?

**Sample Answer:**
"I try to understand their point of view and keep communication professional. If needed, I find a way to work together respectfully."

**Practice Tips:**
- Show professionalism and empathy
- Demonstrate conflict resolution skills
- Avoid negative examples

### 18. What kind of work environment do you prefer?

**Sample Answer:**
"I enjoy environments that are collaborative, open to new ideas, and where people support each other to reach common goals."

**Practice Tips:**
- Research the company culture
- Show you'll fit in well
- Be positive and enthusiastic

---

## Skills & Development Questions

### 19. How do you keep your skills up to date?

**Sample Answer:**
"I take online courses, attend webinars, and follow industry blogs and newsletters."

**Practice Tips:**
- Show commitment to learning
- Give specific examples
- Mention relevant courses or certifications

### 20. How do you approach learning something new?

**Sample Answer:**
"I break it down into small parts, do research, and apply what I learn through practice. I also ask questions when needed."

**Practice Tips:**
- Describe your learning process
- Show you're proactive
- Give a specific example

### 21. How do you stay organized?

**Sample Answer:**
"I use task management apps, set reminders, and plan my week in advance to stay on top of everything."

**Practice Tips:**
- Describe specific organization tools
- Show systematic approach
- Give concrete examples

---

## Situational Questions

### 22. How do you handle tight deadlines?

**Sample Answer:**
"I stay focused, prioritize carefully, and communicate with my team if there are any blockers."

**Practice Tips:**
- Show you work well under pressure
- Demonstrate time management skills
- Include teamwork if relevant

### 23. Can you describe a successful project you worked on?

**Sample Answer:**
"I helped lead a website redesign that improved user experience and increased traffic by 40%. I managed communication between the design and development teams."

**Practice Tips:**
- Use specific metrics and results
- Highlight your role and contributions
- Choose a relevant example

### 24. Have you ever disagreed with a boss?

**Sample Answer:**
"Yes, and I handled it professionally by discussing my concerns respectfully and focusing on what was best for the team."

**Practice Tips:**
- Show you can handle disagreement professionally
- Focus on solution-oriented approach
- Demonstrate respect for authority

### 25. Describe your ideal manager.

**Sample Answer:**
"Someone who's clear about goals, gives feedback regularly, and trusts their team to get things done."

**Practice Tips:**
- Focus on positive leadership qualities
- Show what helps you perform best
- Avoid criticizing past managers

---

## Practical Questions

### 26. What do you do outside of work?

**Sample Answer:**
"I enjoy hiking, reading, and learning about new technologies. I also volunteer on weekends at a local shelter."

**Practice Tips:**
- Show you're well-rounded
- Include activities that show positive traits
- Keep it brief and professional

### 27. Are you willing to relocate?

**Sample Answer:**
"Yes, I'm open to relocating if the opportunity is right."

**Practice Tips:**
- Be honest about your flexibility
- Consider the role requirements
- Show you've thought about it

### 28. What are your salary expectations?

**Sample Answer:**
"I'm open to discussing a fair salary based on the role and my experience. I'm sure we can agree on something that works for both of us."

**Practice Tips:**
- Research typical salaries for the role
- Show flexibility
- Focus on mutual benefit

---

## Questions for Them

### 29. Do you have any questions for us?

**Sample Answer:**
"Yes! I'd love to know more about the team I'd be working with and what success looks like in this role after 6 months."

**Great Questions to Ask:**
- "What does a typical day look like in this role?"
- "What are the biggest challenges facing the team?"
- "How do you measure success in this position?"
- "What opportunities are there for professional development?"
- "Can you tell me about the company culture?"

---

## Final Question

### 30. What makes you unique?

**Sample Answer:**
"I combine strong technical skills with empathy and communication, which helps me connect with both clients and colleagues effectively."

**Practice Tips:**
- Highlight your unique combination of skills
- Show what sets you apart
- Connect to the job requirements

---

## Interview Preparation Checklist

### Before the Interview:
- ✅ Research the company and role
- ✅ Practice answers out loud
- ✅ Prepare questions to ask them
- ✅ Plan your outfit
- ✅ Test your technology (for video interviews)

### During the Interview:
- ✅ Listen carefully to each question
- ✅ Take a moment to think before answering
- ✅ Give specific examples when possible
- ✅ Ask clarifying questions if needed
- ✅ Show enthusiasm and interest

### After the Interview:
- ✅ Send a thank-you email within 24 hours
- ✅ Follow up as promised
- ✅ Reflect on what went well and what to improve

---

## Practice with AI

Use VivaLingua's AI conversation practice to:
- **Rehearse your answers out loud**
- **Get feedback on pronunciation and fluency**
- **Practice handling unexpected questions**
- **Build confidence for the real interview**

> **Remember:** The key to interview success is preparation and practice. Use these sample answers as a starting point, but make them your own with your personal experiences and achievements.

**Good luck with your interview!**
      `
    }
  }

  const guide = guides[slug as keyof typeof guides] || guides['first-conversation-with-ai']

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-500/20 text-green-400'
      case 'intermediate':
        return 'bg-yellow-500/20 text-yellow-400'
      case 'advanced':
        return 'bg-red-500/20 text-red-400'
      default:
        return 'bg-slate-500/20 text-slate-400'
    }
  }

  const formatContent = (content: string) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('# ')) {
          return <h1 key={index} className="text-3xl font-bold text-white mb-6 mt-8">{line.slice(2)}</h1>
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} className="text-2xl font-semibold text-white mb-4 mt-8">{line.slice(3)}</h2>
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="text-xl font-medium text-white mb-3 mt-6">{line.slice(4)}</h3>
        }
        if (line.startsWith('> ')) {
          return (
            <blockquote key={index} className="border-l-4 border-purple-500 pl-4 italic text-slate-300 my-4 bg-slate-800/30 py-2">
              {line.slice(2)}
            </blockquote>
          )
        }
        if (line.startsWith('- ✅ ')) {
          return (
            <li key={index} className="text-slate-300 ml-4 mb-2 flex items-start">
              <span className="text-green-400 mr-3 mt-1">✅</span>
              {line.slice(5)}
            </li>
          )
        }
        if (line.startsWith('- ')) {
          return (
            <li key={index} className="text-slate-300 ml-4 mb-2 flex items-start">
              <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              {line.slice(2)}
            </li>
          )
        }
        if (line.startsWith('**') && line.endsWith('**')) {
          return <p key={index} className="font-semibold text-white mb-2">{line.slice(2, -2)}</p>
        }
        if (line.trim() === '') {
          return <br key={index} />
        }
        if (line.trim() === '---') {
          return <hr key={index} className="border-slate-700 my-8" />
        }
        return <p key={index} className="text-slate-300 mb-4 leading-relaxed">{line}</p>
      })
  }

  return (
    <div className="px-4 py-12 sm:px-6 lg:px-8 animate-fade-in">
      <div className="mx-auto max-w-4xl">
        {/* Back Navigation */}
        <Link
          to="/guides"
          className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Guides
        </Link>

        {/* Header */}
        <header className="mb-12">
          <div className="mb-4">
            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${getDifficultyColor(guide.difficulty)}`}>
              {guide.difficulty.charAt(0).toUpperCase() + guide.difficulty.slice(1)}
            </span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{guide.title}</h1>
          <p className="text-xl text-slate-300 mb-6">{guide.description}</p>
          
          {/* Meta Information */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-400">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              {guide.readTime}
            </div>
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2" />
              {guide.author}
            </div>
            <div>
              Last updated: {guide.lastUpdated}
            </div>
          </div>
        </header>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-12">
          <a
            href="https://vivalingua.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 group"
          >
            <Play className="h-4 w-4 mr-2" />
            Start Practice Session
          </a>
          <button className="inline-flex items-center px-6 py-3 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200">
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark as Complete
          </button>
        </div>

        {/* Content */}
        <article className="prose prose-invert max-w-none">
          <div className="bg-slate-800/30 rounded-xl p-8 border border-slate-700">
            {formatContent(guide.content)}
          </div>
        </article>

        {/* Related Guides */}
        <section className="mt-16">
          <h2 className="text-2xl font-semibold text-white mb-6">Related Guides</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/guides/business-english-conversations"
              className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-300 card-hover"
            >
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                Business English Conversations
              </h3>
              <p className="text-slate-300 mb-4">Practice professional English for workplace success</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">12 min read</span>
                <span className="bg-yellow-500/20 text-yellow-400 text-xs font-medium px-2 py-1 rounded-full">
                  Intermediate
                </span>
              </div>
            </Link>
            <Link
              to="/guides/confidence-building-techniques"
              className="group bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-300 card-hover"
            >
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                Confidence Building Techniques
              </h3>
              <p className="text-slate-300 mb-4">Overcome speaking anxiety and build confidence with AI practice</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">9 min read</span>
                <span className="bg-green-500/20 text-green-400 text-xs font-medium px-2 py-1 rounded-full">
                  Beginner
                </span>
              </div>
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}

export default GuideDetailPage